import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthUserId } from '../../contexts/AuthContext';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useIsPhiWorkspace } from '../user/useIsPhiWorkspace';
import { useCanWrite } from '../user/useWorkspaceRole';
import { useChatConnectorsStore } from '../../stores/chatConnectorsStore';
import { useConnections } from './useConnectors';
import {
  reduceChatMessage,
  type ChatActivity,
  type ChatActivityStatus,
  type ChatMessage,
} from './agentChatReducer';
import { useAgentChatCore } from './useAgentChatCore';
import { useAgentChatJobOutcomes } from './useAgentChatJobOutcomes';
import { useAgentChatSessionLifecycle, type LeaveLiveTurn } from './useAgentChatSessionLifecycle';
import { useAgentChatHistory } from './useAgentChatHistory';
import { useAgentChatTurn } from './useAgentChatTurn';
import { useAgentChatHitlReplies } from './useAgentChatHitlReplies';
import type { UseAgentChatReturn } from './useAgentChat.types';

// Re-export the reducer + types so existing callers (BirdMessage,
// BirdAgentActivity, the unit tests) keep importing them from this
// module. The reducer itself lives in ``agentChatReducer.ts`` to keep
// it free of any context / Firebase imports — that way the unit test
// for the pure function doesn't drag the workspace+auth graph through
// the test runner, which produced flaky Firebase-init failures in CI.
export { reduceChatMessage };
export type { ChatActivity, ChatActivityStatus, ChatMessage };

// Same bargain for the hook's own contract types: they moved to a sibling
// module to keep this file clear of the 1000-line guard, and are re-exported
// here so no caller's import path changed.
export type {
  ChatHistoryState,
  ChatSessionCreationSource,
  ChatTurnIds,
  RefusedDraft,
  TurnStillWorking,
} from './useAgentChat.types';

/**
 * One chat session's state and transport.
 *
 * Call it from a component that CANNOT unmount while a turn is in flight —
 * everything here is component state, so an unmount destroys the live turn and
 * the reader keeps streaming into nothing. Any surface that can be closed,
 * collapsed or navigated away from consumes `useAgentChatSession` instead,
 * which reads the instance `AgentChatProvider` holds above the whole app. The
 * onboarding Ask-Bower step is the one direct caller: it owns the screen for as
 * long as its conversation exists.
 */
export function useAgentChat({
  enabled = true,
}: { readonly enabled?: boolean } = {}): UseAgentChatReturn {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  // The composer tools picker (and its per-turn toggles) only ships to
  // users not in a PHI workspace. PHI workspaces are blocked
  // server-side, but stripping the wire fields here keeps the picker
  // hidden + the request shape pre-connectors-clean.
  const isPhiWorkspace = useIsPhiWorkspace();
  const connectorsEnabled = !isPhiWorkspace;
  const canWrite = useCanWrite();
  const connectionsQuery = useConnections(enabled);
  const enabledFromActive = useChatConnectorsStore((s) => s.enabledFromActive);
  const webSearchEnabled = useChatConnectorsStore((s) => s.webSearchEnabled);
  const userId = useAuthUserId();
  const { messages, isStreaming, isUploadingTurn, currentAgent, sessionId, refusedDraft, chat } =
    useAgentChatCore(workspaceId, userId);

  // Effect order is load-bearing; keep these four calls in this order. The
  // job-outcome listener, then the workspace reset (before persistence and the
  // seed read the session it picks), then the history query and its seed.
  useAgentChatJobOutcomes(chat, sessionId);
  const staleLeftTurnHistory = useCallback<LeaveLiveTurn>(
    (leftWorkspaceId, leftSessionId) => {
      if (!leftWorkspaceId) return;
      // The read cached before the send is otherwise fresh for a minute, and the
      // seed copies one read per session, so the return would show neither the
      // question nor "Still working". Stale, the return refetches and the seed
      // waits for it (`isFetching`). No refetch now: the server may not have it.
      void queryClient.invalidateQueries({
        queryKey: ['session-history', leftWorkspaceId, leftSessionId],
        exact: true,
        refetchType: 'none',
      });
    },
    [queryClient],
  );
  const { switchSession, createNewSession } = useAgentChatSessionLifecycle(
    chat,
    workspaceId,
    userId,
    sessionId,
    staleLeftTurnHistory,
  );
  const { isLoadingHistory, historyState, retryHistory, turnStillWorking } = useAgentChatHistory({
    chat,
    enabled,
    workspaceId,
    sessionId,
    messages,
    isStreaming,
  });

  // No effects below this line: only callbacks and state.
  const { sendMessage, cancelMessage } = useAgentChatTurn({
    chat,
    enabled,
    workspaceId,
    sessionId,
    isStreaming,
    queryClient,
    canWrite,
    connectorsEnabled,
    connections: connectionsQuery.data,
    enabledFromActive,
    webSearchEnabled,
  });
  const { respondToConfirmation, respondingConfirmationId, respondToChoice, respondingChoiceId } =
    useAgentChatHitlReplies(chat, sendMessage);
  const clearRefusedDraft = useCallback(() => chat.setRefusedDraft(null), [chat]);

  return {
    messages,
    isStreaming,
    isUploadingTurn,
    turnStillWorking,
    refusedDraft,
    clearRefusedDraft,
    isLoadingHistory,
    historyState,
    retryHistory,
    currentAgent,
    sessionId,
    sendMessage,
    cancelMessage,
    respondToConfirmation,
    respondingConfirmationId,
    respondToChoice,
    respondingChoiceId,
    createNewSession,
    switchSession,
  };
}
